import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Breadcrumb from '../components/shared/Breadcrumb';
import { MdAssignmentReturn } from 'react-icons/md';
import { listTickets, Ticket } from '../services/ticketsApi';
import '../styles/ReturnsPage.css';

/**
 * VP-CUST-003 Returns tab (ecommerce half).
 *
 * Same minimal, ticket-backed approach as vin-pulse-os-web's
 * CustomerReturnsTab.tsx: there is no structured returns/RMA backend entity
 * (Phase 2 scope). This surfaces the customer's own support tickets filed
 * under the existing 'returns' category. GET /tickets already scopes
 * Customer-role requests to their own customer_id server-side.
 */
const ReturnsPage: React.FC = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    listTickets({ category: 'returns', limit: 50 })
      .then((res) => setTickets(res.data))
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load returns'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="returns-page">
      <Breadcrumb />

      <section className="returns-page__header-section">
        <div className="returns-page__header-inner">
          <div>
            <h1 className="returns-page__title">Returns &amp; Disputes</h1>
            <p className="returns-page__subtitle">{tickets.length} return request{tickets.length === 1 ? '' : 's'}</p>
          </div>
          <button
            type="button"
            className="returns-page__new-btn"
            onClick={() => navigate('/report-issue?category=returns')}
          >
            + New Return Request
          </button>
        </div>
      </section>

      <section className="returns-page__list-section">
        {loading ? (
          <div className="returns-page__empty">
            <h2>Loading…</h2>
          </div>
        ) : error ? (
          <div className="returns-page__empty">
            <MdAssignmentReturn className="returns-page__empty-icon" />
            <h2>Couldn't load return requests</h2>
            <p>{error}</p>
          </div>
        ) : tickets.length === 0 ? (
          <div className="returns-page__empty">
            <MdAssignmentReturn className="returns-page__empty-icon" />
            <h2>No return requests yet</h2>
            <p>If you need to return an item or file a dispute, start a new request above.</p>
          </div>
        ) : (
          <div className="returns-page__table-wrapper">
            <table className="returns-page__table">
              <thead>
                <tr>
                  <th>Ticket #</th>
                  <th>Order</th>
                  <th>Subject</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>Opened</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((t) => (
                  <tr key={t.id}>
                    <td className="returns-page__num">{t.ticket_number}</td>
                    <td>{t.order_id ? `VIN-${t.order_id}` : '—'}</td>
                    <td>{t.subject}</td>
                    <td>
                      <span className="returns-page__badge">{t.status.replace('_', ' ')}</span>
                    </td>
                    <td>
                      <span className="returns-page__badge">{t.priority}</span>
                    </td>
                    <td>{new Date(t.opened_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default ReturnsPage;
